import React, { useState, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Button, Box, Popper } from '@mui/material';
import { alertActions, masterActions } from '_store';
import { CustomFormControl, AutocompleteInput } from '_components';
import { newRoleSchema } from '_utils/validationSchema';
import Grid from "@material-ui/core/Grid";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Checkbox from '@mui/material/Checkbox';
import { ERR_MSG } from 'constants/labels';

const AddRole = ({ handleFetch, isDigitalAdmin }) => {
    const header = "Add Role";
    const dispatch = useDispatch();
    // const portals = useSelector((x) => x.master?.portalData);
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const auth = useSelector(x => x.auth.value);
    const data = auth?.Data;
    const user = data?.UserDetails;
    const canBeOpen = open && Boolean(anchorEl);
    const id = canBeOpen ? 'simple-popper' : undefined;
    console.log('user', user?.UserAccess);
    const portals = data?.UserAccess?.filter(access =>
        ["admin", "reviewer", "owner", "digital-admin"].includes(access.Role.toLowerCase())
    );

    const portalData = (!portals?.loading && !portals?.error) ? portals?.map(x => ({
        label: x.PortalName,
        value: x.PortalId
    })).sort((a, b) => a.label.localeCompare(b.label)) : [];

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        setError,
        clearErrors,
        reset
    } = useForm({
        resolver: yupResolver(newRoleSchema),
        defaultValues: {
            IsCustomerRole: isDigitalAdmin ? false : true,
            IsWglRole: isDigitalAdmin ? true : false
        }
    });
    const isWglRole = useWatch({
        control,
        name: "IsWglRole"
    });

    const selectedPortalId = useWatch({
        control,
        name: "PortalId"
    });

    const roleName = useWatch({ control, name: "RoleName" });

    useEffect(() => {
        if (isWglRole) {
            setValue("IsCustomerRole", false);
            clearErrors("IsCustomerRole");
        }
    }, [isWglRole, setValue, clearErrors]);
    useEffect(() => {
        if (selectedPortalId) {
            // Reset IsCustomerRole to false on every portal change
            setValue("IsCustomerRole", false);
            clearErrors("IsCustomerRole"); // clear error if any

            // Show error if PortalId is 4 and IsCustomerRole not checked
            if (selectedPortalId === 4) {
                setError("IsCustomerRole", {
                    type: "manual",
                    message: "Customer Role must be checked for Marketer"
                });
            }
        }
    }, [selectedPortalId, setValue, setError, clearErrors]);
    useEffect(() => {
        const fetchData = async () => {
            dispatch(alertActions.clear());
            try {
                dispatch(masterActions.getPortalData()).unwrap();
            } catch (error) {
                dispatch(alertActions.error({
                    message: error.message ? ERR_MSG["073"] : error,
                    header,
                }));
            }
        };
        fetchData();
    }, [dispatch]);

    const onSubmit = async (data) => {
        handleOnSubmit(data);
    };

    const handleOnSubmit = async (data) => {
        dispatch(alertActions.clear());
        try {

            data = { ...data, RoleDescription: data.RoleName, RoleID: 0, PortalRoleID: 0, IsActive: true, CreatedBy: user.firstName, IsWglRole: data.IsWglRole, IsCustomerRole: data.IsCustomerRole }
            await dispatch(masterActions.saveRole(data)).unwrap();
            await handleFetch();
            await setOpen(false);
            await dispatch(alertActions.success({
                showAfterRedirect: true,
                message: "Role created successfully.",
                header: header
            }));
            reset({
                PortalId: null,
                RoleName: '',
                IsCustomerRole: isDigitalAdmin ? false : true,
                IsWglRole: isDigitalAdmin ? true : false
            });
        } catch (error) {
            dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header, }));
        }
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setOpen((previousOpen) => !previousOpen);
        reset({
            PortalId: null,
            RoleName: '',
            IsCustomerRole: isDigitalAdmin ? false : true,
            IsWglRole: isDigitalAdmin ? true : false
        });
    };

    const isFormValid = !!roleName && !!selectedPortalId;

    return (<>
        <Button
            variant="contained"
            className='Download margin-25'
            color="primary" aria-describedby={id} onClick={handleClick}
        ><AddCircleOutlineIcon /> Role
        </Button>
        <Popper id={id} open={open} anchorEl={anchorEl} className="Filtercontainer">
            <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }} className="Filtercontainerinner Border">
                <form onSubmit={handleSubmit(onSubmit)} className='Registrationcontainer'>
                    <Typography component="div" className='userprofilelist'>
                        <Grid container direction="row" spacing={{ xs: 2, md: 3 }} >
                            <Grid size={{ xs: 12, sm: 6, md: 6 }}  >
                                <Typography variant="h2" className='userprofilelistcontent'>Add New Role</Typography>
                            </Grid>
                        </Grid>
                    </Typography>
                    <AutocompleteInput
                        control={control}
                        name="PortalId"
                        label="Select Portal"
                        options={portalData}
                    />
                    <CustomFormControl
                        id="RoleName"
                        label="Role Name"
                        type="text"
                        register={register}
                        errors={errors}
                    />
                    <Grid>
                        <div>
                            <Controller
                                name="IsWglRole"
                                control={control}
                                defaultValue={true}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        checked={field.value}
                                        disabled={!isDigitalAdmin}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        color={isDigitalAdmin ? "primary" : "secondary" }
                                    />
                                )}
                            />
                            <Typography variant="p" sx={{ marginLeft: "5px", color: isDigitalAdmin ? 'text.primary' : 'text.disabled'}} className='userprofilelistcontent'>
                                WGL Role
                            </Typography>
                        </div>
                        {selectedPortalId === 4 && !isWglRole && (
                            <div>
                                <Controller
                                    name="IsCustomerRole"
                                    control={control}
                                    render={({ field }) => (
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            color={!isWglRole ? "primary" : "secondary" }
                                            disabled={isWglRole}
                                            onChange={(e) => {
                                                field.onChange(e.target.checked);
                                                if (!e.target.checked && selectedPortalId === 4) {
                                                    setError("IsCustomerRole", {
                                                        type: "manual",
                                                        message: "Customer Role must be checked for Marketer"
                                                    });
                                                } else {
                                                    clearErrors("IsCustomerRole");
                                                }
                                            }}
                                        />
                                    )}
                                />
                                <Typography variant="p" className='userprofilelistcontent'>Customer Role</Typography>
                            </div>
                        )}


                    </Grid>
                    <Box className="popup-button" spacing={{ xs: 2, md: 3 }} >
                        <Button
                            type="submit"
                            variant="contained"
                            className='cancelButton'
                            color="primary"
                            onClick={handleClick}
                        >
                            cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            className='Savebutton'
                            color="primary"
                            disabled={!isFormValid}
                        >
                            Save
                        </Button>
                    </Box>
                </form>
            </Box>
        </Popper>
    </>);
};

export default AddRole;